import { upsertJobInList } from "./printerMappers";

/**
 * Per-printer job history: the printer page fetches its own page of jobs from
 * the server (scoped to one printer + an optional creation-date window) instead
 * of filtering the global "latest 100" snapshot, which only ever covers roughly
 * the current day across all printers.
 *
 * Without a date range the page is the printer's latest 100 jobs. With a range
 * the whole range is requested in one page — a busy printer prints ~1.7K jobs
 * in 90 days, so DATE_RANGE_JOBS_LIMIT is a safety ceiling (mirrors the
 * server's max), not a pagination unit. The header tells the user to narrow the
 * dates only when that ceiling is actually hit.
 */
export const LATEST_JOBS_PAGE_LIMIT = 100;
export const DATE_RANGE_JOBS_LIMIT = 5000;

export const DATE_RANGE_PRESETS = [
  { key: "latest", label: `Latest ${LATEST_JOBS_PAGE_LIMIT}`, days: null },
  { key: "7d", label: "7 days", days: 7 },
  { key: "30d", label: "30 days", days: 30 },
  { key: "60d", label: "60 days", days: 60 },
  { key: "90d", label: "90 days", days: 90 },
];

export const CUSTOM_DATE_RANGE_KEY = "custom";

/** `fromDate` / `toDate` are `YYYY-MM-DD` strings (native date-input values), "" = unbounded. */
export const DEFAULT_DATE_RANGE = { presetKey: "latest", fromDate: "", toDate: "" };

const leadingZero = (num) => `0${num}`.slice(-2);

/** Local calendar date → the `YYYY-MM-DD` value a native date input expects. */
export function toDateInputValue(date) {
  return `${date.getFullYear()}-${leadingZero(date.getMonth() + 1)}-${leadingZero(date.getDate())}`;
}

function parseDateInputValue(value) {
  if (!value || typeof value !== "string") return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  return { year, month, day };
}

/** Start of the given local calendar day, as a Date. */
export function startOfLocalDay(inputValue) {
  const parts = parseDateInputValue(inputValue);
  if (!parts) return null;
  return new Date(parts.year, parts.month - 1, parts.day, 0, 0, 0, 0);
}

/** End of the given local calendar day (23:59:59.999), as a Date. */
export function endOfLocalDay(inputValue) {
  const parts = parseDateInputValue(inputValue);
  if (!parts) return null;
  return new Date(parts.year, parts.month - 1, parts.day, 23, 59, 59, 999);
}

/**
 * Resolve a preset chip into concrete input values, so the from/to inputs always
 * show the dates a preset actually covers. "N days" means today and the N days
 * before it; "latest" clears both bounds (plain latest-N page for the printer).
 */
export function getPresetDateRange(presetKey, now = new Date()) {
  const preset = DATE_RANGE_PRESETS.find((candidate) => candidate.key === presetKey);
  if (!preset || preset.days === null) {
    return { ...DEFAULT_DATE_RANGE };
  }
  const fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - preset.days);
  return {
    presetKey,
    fromDate: toDateInputValue(fromDate),
    toDate: toDateInputValue(now),
  };
}

/** True when at least one side of the range is a valid date (i.e. the user is looking at history, not "latest"). */
export function hasDateBounds({ fromDate = "", toDate = "" } = {}) {
  return !!(startOfLocalDay(fromDate) || endOfLocalDay(toDate));
}

/** Page size to request (and to compare the result against for the "ceiling hit" hint). */
export function getJobsLimitForDateRange(dateRange) {
  return hasDateBounds(dateRange) ? DATE_RANGE_JOBS_LIMIT : LATEST_JOBS_PAGE_LIMIT;
}

/** Request path for one printer's job page — `/dashboardActiveJobs?printerId=…&fromDate=…&toDate=…&limit=…`. */
export function buildPrinterJobsRequestPath({ printerId, fromDate = "", toDate = "" }) {
  const params = new URLSearchParams();
  if (printerId) params.set("printerId", printerId);
  const fromBound = startOfLocalDay(fromDate);
  if (fromBound) params.set("fromDate", fromBound.toISOString());
  const toBound = endOfLocalDay(toDate);
  if (toBound) params.set("toDate", toBound.toISOString());
  if (fromBound || toBound) params.set("limit", String(DATE_RANGE_JOBS_LIMIT));
  const query = params.toString();
  return query ? `/dashboardActiveJobs?${query}` : "/dashboardActiveJobs";
}

/** Whether a (mapped) job's creation time falls inside the range; an empty bound is open-ended. */
export function isJobWithinDateRange(job, { fromDate = "", toDate = "" } = {}) {
  const createdAt = job?.unix instanceof Date ? job.unix : new Date(job?.unix || 0);
  const createdMs = createdAt.getTime();
  if (Number.isNaN(createdMs)) return false;
  const fromBound = startOfLocalDay(fromDate);
  if (fromBound && createdMs < fromBound.getTime()) return false;
  const toBound = endOfLocalDay(toDate);
  if (toBound && createdMs > toBound.getTime()) return false;
  return true;
}

/**
 * Fold live socket updates (already mapped, already filtered to this printer)
 * into the printer's fetched page. A job that is already on the page is always
 * refreshed (status changes must show even for out-of-range rows); a job that
 * is new to the page is only added when it falls inside the selected range, so
 * a 30-day view doesn't grow today's jobs when the user is looking at last month.
 */
export function mergeLiveJobsIntoPrinterJobs(currentJobs, liveJobs, dateRange = DEFAULT_DATE_RANGE) {
  let merged = currentJobs || [];
  (liveJobs || []).forEach((liveJob) => {
    if (!liveJob?.id) return;
    const alreadyOnPage = merged.some((job) => job.id === liveJob.id);
    if (alreadyOnPage || isJobWithinDateRange(liveJob, dateRange)) {
      merged = upsertJobInList(merged, liveJob);
    }
  });
  return merged;
}
